import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

// Mirrors backend/src/modules/activity/activity-feed.ts.

export const ACTIVITY_CATEGORIES = ['invoices', 'reminders', 'receipts', 'payments', 'time', 'timesheets'] as const;
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

export interface ActivityItem {
  id: string;
  /** ISO instant. */
  at: string;
  category: ActivityCategory;
  title: string;
  detail: string | null;
  actor: string | null;
  amount: number | null;
  link: string | null;
  tone: 'ok' | 'warn' | 'bad' | null;
}

export interface ActivityFeed {
  from: string;
  to: string;
  /** The org's zone — every time on the screen is read in it. */
  timeZone: string;
  category: ActivityCategory | 'all';
  items: ActivityItem[];
  counts: Record<ActivityCategory, number>;
  total: number;
  /** A source hit its per-request cap — narrow the dates to see the rest. */
  truncated: boolean;
}

/**
 * Whether the signed-in person may open Activity (an org owner or admin). The
 * server decides; this only hides the nav item from people it would refuse.
 * Never a 403, so it is safe to ask on every page.
 */
export function useActivityAccess(): boolean {
  const q = useQuery({
    queryKey: ['activity', 'access'],
    queryFn: () => api.get<{ canView: boolean }>('/activity/access'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return q.data?.canView === true;
}

export function useActivityFeed(input: { category: ActivityCategory | 'all'; from: string; to: string }) {
  return useQuery({
    queryKey: ['activity', 'feed', input.category, input.from, input.to],
    queryFn: () =>
      api.get<ActivityFeed>('/activity', {
        params: {
          category: input.category === 'all' ? undefined : input.category,
          from: input.from || undefined,
          to: input.to || undefined,
        },
      }),
    placeholderData: keepPreviousData,
    // A 4xx is an answer (not an admin · more than 92 days), not a blip — show
    // it at once instead of asking three more times.
    retry: (count, error) => {
      const status = error instanceof ApiError ? error.status : 0;
      return (status === 0 || status >= 500) && count < 2;
    },
  });
}
